import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import {
  readPageSetup,
  readSlideBackgrounds,
  standardFolienmasse,
} from "@/office/powerpoint/design";
import { allShapes, readPresentation } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";
import { criterion, fromCriteria } from "@/core/check";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_BILDER } from "../makros";

export const lektionZuschneiden: Lesson = {
  id: "zuschneiden",
  title: "Zuschneiden statt verzerren",
  summary: "Ein Bild verändert seine Größe – nie seine Proportionen.",
  durationMinutes: 9,
  learningGoals: [
    "Bilder proportional skalieren",
    "Über Zuschneiden den passenden Bildausschnitt wählen",
    "Objekte innerhalb des Folienrands halten",
  ],
  steps: [
    {
      id: "proportionen",
      title: "Ecken statt Kanten",
      explanation: [
        "Ein verzerrtes Bild fällt sofort auf, auch wenn niemand benennen kann, warum: " +
          "Gesichter wirken zu breit, Kreise werden zu Ellipsen, Schrift im Bild kippt " +
          "aus dem Lot. Der Grund ist fast immer derselbe – jemand hat das Bild an einer " +
          "Seitenkante gezogen statt an einer Ecke.",
        "An den Eckpunkten skaliert PowerPoint proportional, an den Seitenkanten nicht. " +
          "Wer sich angewöhnt, Bilder ausschließlich an den Ecken zu greifen, hat das " +
          "Problem ein für alle Mal gelöst. Hält man dabei zusätzlich die Strg-Taste, " +
          "wächst das Bild von der Mitte aus – praktisch, wenn es zentriert bleiben soll.",
        "Soll ein Bild ein bestimmtes Format ausfüllen, etwa einen quadratischen Bereich, " +
          "ist Zuschneiden der richtige Weg, nicht Stauchen. „Bildformat“ → „Zuschneiden“ " +
          "→ „Seitenverhältnis“ bietet fertige Verhältnisse an; anschließend lässt sich " +
          "der sichtbare Ausschnitt innerhalb des Rahmens verschieben.",
        "Besonders elegant ist „Zuschneiden“ → „Auf Form zuschneiden“: Das Bild nimmt " +
          "die Form eines Kreises, eines abgerundeten Rechtecks oder eines Pfeils an, " +
          "ohne dass sein Inhalt verzerrt wird.",
      ],
      keyPoints: [
        "Immer an den Eckpunkten skalieren, nie an den Seitenkanten.",
        "Strg beim Ziehen skaliert von der Mitte aus.",
        "Bildformat → Zuschneiden → Seitenverhältnis oder Auf Form zuschneiden.",
      ],
      task: {
        instruction:
          "Schneide mindestens ein Bild über „Bildformat“ → „Zuschneiden“ zu. Bei allen Bildern " +
          "soll das Seitenverhältnis gesperrt bleiben.",
        hint: "Bild anklicken, Bildformat → Zuschneiden, an den schwarzen Balken ziehen, Esc. Das Seitenverhältnis steht unter Größe → kleiner Pfeil.",
        macro: MAKRO_BILDER,
        check: () =>
          pruefeMitMakro(MAKRO_BILDER, (bericht) => {
            const bilder = bericht.zahl("bilder") ?? 0;
            const zugeschnitten = bericht.zahl("zugeschnitten") ?? 0;
            const gesperrt = bericht.zahl("seitenverhaeltnisGesperrt") ?? 0;
            return fromCriteria(
              [
                criterion(`Bilder in der Präsentation: ${bilder}`, bilder > 0),
                criterion(`Mindestens ein Bild ist zugeschnitten (gefunden: ${zugeschnitten})`, zugeschnitten > 0),
                criterion(`Bei allen Bildern ist das Seitenverhältnis gesperrt (${gesperrt} von ${bilder})`, bilder > 0 && gesperrt === bilder),
              ],
              {
                success: "Zugeschnitten statt gestaucht – die Proportionen bleiben, wie sie sind.",
                failure: "Zuschnitt oder gesperrtes Seitenverhältnis fehlen noch.",
                hint: "Größe und Position → „Seitenverhältnis sperren“ anhaken.",
              },
            );
          }),
      },
    },
    {
      id: "im-folienrand",
      title: "Innerhalb der Folie bleiben",
      explanation: [
        "Objekte, die über den Folienrand hinausragen, sind ein häufiger und leicht zu " +
          "übersehender Fehler. Im Bearbeitungsfenster sieht man den Überstand als " +
          "grauen Bereich – in der Präsentation ist er schlicht abgeschnitten.",
        "Manchmal ist das gewollt: Ein Bild, das bewusst über den Rand hinausläuft, wirkt " +
          "großzügig. Meistens ist es aber ein Versehen, entstanden beim Skalieren mit der " +
          "Maus. Der zuverlässigste Weg, es zu finden, ist die Foliensortierung – dort " +
          "sieht man nur den sichtbaren Bereich.",
        "Exakte Werte lassen sich über „Bildformat“ → „Größe“ und den kleinen Pfeil " +
          "rechts unten einstellen. Der Bereich „Position“ zeigt dort die Koordinaten " +
          "des Objekts relativ zur oberen linken Folienecke.",
      ],
      keyPoints: [
        "Der graue Bereich außerhalb der Folie wird beim Präsentieren abgeschnitten.",
        "Bildformat → Größe → Position zeigt und setzt exakte Koordinaten.",
        "Die Foliensortierung zeigt nur, was wirklich sichtbar ist.",
      ],
      task: {
        instruction:
          "Prüfe deine Folien: Kein Bild und keine Form soll unbeabsichtigt über den " +
          "Folienrand hinausragen.",
        hint: "Über Bildformat → Größe → Position lassen sich die Koordinaten genau setzen.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Position und Größe einer Form",
              "1.4",
              "Sieh in der Foliensortierung nach, ob Objekte angeschnitten wirken.",
            );
          }

          const gelesen = await readPageSetup();
          const masse = gelesen ?? standardFolienmasse;
          const exakt = gelesen !== null;

          // Ein Punkt Toleranz: Randbündige Objekte liegen manchmal minimal
          // außerhalb, ohne dass etwas sichtbar fehlt.
          const toleranz = 1;
          const draussen = allShapes(snapshot).filter((form) => {
            if (form.left === null || form.top === null) return false;
            const rechts = form.left + (form.width ?? 0);
            const unten = form.top + (form.height ?? 0);
            return (
              form.left < -toleranz ||
              form.top < -toleranz ||
              rechts > masse.slideWidth + toleranz ||
              unten > masse.slideHeight + toleranz
            );
          });

          if (draussen.length === 0) {
            return pass(
              `Alle Objekte liegen innerhalb der Folie (${Math.round(masse.slideWidth)} × ${Math.round(masse.slideHeight)} pt` +
                `${exakt ? "" : ", angenommen als Breitbildformat"}).`,
            );
          }

          const erstes = draussen[0];
          return fail(
            `${draussen.length === 1 ? "Ein Objekt ragt" : `${draussen.length} Objekte ragen`} über den Folienrand hinaus, ` +
              `zum Beispiel „${erstes.name ?? "unbenannt"}“ auf Folie ${erstes.slideIndex + 1}.`,
            exakt
              ? "Objekt markieren und unter „Bildformat“ → „Größe“ → „Position“ die Koordinaten korrigieren. Ist der Überstand gewollt, kannst du den Schritt so lassen und abhaken."
              : "Die Foliengröße ließ sich nicht auslesen, gerechnet wurde mit dem Breitbildformat 960 × 540 pt. Prüfe den Befund deshalb kurz selbst.",
          );
        },
      },
    },
    {
      id: "hintergrundbilder",
      title: "Bilder als Hintergrund",
      explanation: [
        "Ein Bild, das die ganze Folie füllt, ist ein starkes Gestaltungsmittel – und " +
          "eine Falle für die Lesbarkeit. Schrift auf einem Foto ist fast immer " +
          "irgendwo schlecht lesbar, weil Fotos helle und dunkle Bereiche haben.",
        "Drei Mittel helfen. Erstens: eine halbtransparente Fläche zwischen Bild und " +
          "Schrift legen – eine Form in Schwarz mit 40 % Transparenz macht jede weiße " +
          "Schrift lesbar. Zweitens: das Bild über „Bildformat“ → „Korrekturen“ " +
          "abdunkeln. Drittens: einen ruhigen Bereich des Bildes für die Schrift " +
          "aussuchen und den Text dorthin legen.",
        "Wenn das Bild tatsächlich zum Hintergrund gehört und nicht zum Inhalt, ist der " +
          "sauberere Weg der Folienhintergrund: Rechtsklick auf die Folie → „Hintergrund " +
          "formatieren“ → „Bild- oder Texturfüllung“. Das Bild liegt dann hinter allen " +
          "Objekten und lässt sich nicht versehentlich verschieben.",
      ],
      keyPoints: [
        "Rechtsklick auf die Folie → Hintergrund formatieren → Bild- oder Texturfüllung.",
        "Halbtransparente Fläche zwischen Bild und Schrift sichert die Lesbarkeit.",
        "Bildformat → Korrekturen dunkelt ein zu helles Bild ab.",
      ],
      task: {
        instruction:
          "Setze auf einer Folie ein Bild als Folienhintergrund – über Rechtsklick auf " +
          "die Folie → „Hintergrund formatieren“ → „Bild- oder Texturfüllung“.",
        hint: "Wichtig: nicht das Bild auf die Folie ziehen, sondern es über den Hintergrund einsetzen.",
        check: async () => {
          const hintergruende = await readSlideBackgrounds();

          if (hintergruende === null) {
            return nichtPruefbar(
              "Der Folienhintergrund",
              "1.10",
              "Prüfe selbst über Rechtsklick auf die Folie → „Hintergrund formatieren“, ob dort eine Bildfüllung eingestellt ist.",
            );
          }

          const mitBild = hintergruende.filter(
            (eintrag) => eintrag.fillType === "PictureOrTexture",
          );
          if (mitBild.length > 0) {
            return pass(
              `Folie ${mitBild[0].slideIndex + 1} hat ein Bild als Hintergrund – es liegt hinter allen Objekten und kann nicht verrutschen.`,
            );
          }

          const bekannt = hintergruende.filter((eintrag) => eintrag.fillType !== null);
          return fail(
            bekannt.length === 0
              ? "Für keine Folie ließ sich eine eigene Hintergrundfüllung feststellen – die Folien übernehmen den Hintergrund vom Folienmaster."
              : `Keine Folie nutzt eine Bildfüllung als Hintergrund (gefunden: ${[...new Set(bekannt.map((eintrag) => eintrag.fillType))].join(", ")}).`,
            "Rechtsklick auf die Folie → „Hintergrund formatieren“ → „Bild- oder Texturfüllung“ → „Einfügen“ → „Aus einer Datei“.",
          );
        },
      },
    },
  ],
};
